import type { Money } from '@stage/contracts';

/**
 * Dollars ↔ integer minor-units conversion for the tip-config editor (§22.4/§22.5). Money is stored
 * and transmitted as integer minor units (cents) — NEVER a float. The UI edits amounts in dollars
 * and converts only at the edges: parse on input, format on display.
 */

/**
 * Convert a dollar (major-unit) amount to integer minor units. Uses string-rounded arithmetic so
 * `12.5` becomes `1250` with no binary-float drift. Returns `null` for non-finite/negative input.
 */
export function dollarsToMinor(dollars: number): number | null {
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  // Round in minor units to defeat floating-point representation error (e.g. 12.50 * 100).
  return Math.round(dollars * 100);
}

/** Convert integer minor units back to a dollar (major-unit) number. */
export function minorToDollars(amountMinor: number): number {
  return amountMinor / 100;
}

/** Format minor units for display, e.g. `1250` → `"12.50"` (always two decimal places). */
export function formatMinor(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

/**
 * Format a {@link Money} value with its currency for display, e.g. `{ USD, 1250 }` → `"12.50 USD"`.
 * Uses the ISO currency code as a suffix rather than hard-coding a symbol without locale (§22.4).
 */
export function formatMoney(money: Money): string {
  return `${formatMinor(money.amountMinor)} ${money.currency}`;
}
